// react
import React, { useState, useEffect } from "react";

// react-i18next
import { useTranslation } from "react-i18next";

// @mui
import { Grid, TextField, Button, CircularProgress, Snackbar, Alert, Typography, Slide, Backdrop } from "@mui/material";

// components
import SectionCard from "./SectionCard";

// features/databox
import { getAuthorizedKeys } from "features/databox/getAuthorizedKeys";
import { updateKeyTimeout } from "features/databox/manageAuthorizedKey";

// utils
import { msToHoursMinutes, hoursMinutesToMs } from "utils/timeUtils";

// A ---------------------------------------------------------------------- M

const getButtonStyle = (action) => ({
  py: 0.5,
  borderColor: "divider",
  color: action === "close" ? "error.main" : "primary.main",
  textTransform: "none",
  boxShadow: "none",
  borderRadius: 10,
  "&:hover": {
    borderColor: action === "close" ? "error.dark" : "primary.dark",
    backgroundColor: "action.hover",
  },
});

const Transition = (props) => <Slide {...props} direction="left" />;

const AuthorizedKeysTimeoutSettings = ({ tag }) => {
  const { t } = useTranslation();

  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [globalUpdating, setGlobalUpdating] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchSources = async () => {
      try {
        const keys = await getAuthorizedKeys(tag);
        const keysWithTime = keys.map((key) => {
          const timeout = key.timeout || 14400000;
          const { hours, minutes } = msToHoursMinutes(timeout);
          return { ...key, timeoutHours: hours, timeoutMinutes: minutes };
        });
        setSources(keysWithTime);
      } catch (error) {
        setError("Error retrieving sources.");
        console.error(error);
      } finally {
        setLoading(false);
      }
    };
    fetchSources();
  }, [tag]);

  const handleHoursChange = (keyId, newHours) => {
    setSources((prevSources) => prevSources.map((source) => (source.id === keyId ? { ...source, timeoutHours: newHours } : source)));
  };

  const handleMinutesChange = (keyId, newMinutes) => {
    setSources((prevSources) => prevSources.map((source) => (source.id === keyId ? { ...source, timeoutMinutes: newMinutes } : source)));
  };

  const handleSave = async (keyId) => {
    try {
      setGlobalUpdating(true);
      const source = sources.find((s) => s.id === keyId);
      const newTimeoutMs = hoursMinutesToMs(source.timeoutHours, source.timeoutMinutes);
      await updateKeyTimeout(tag, keyId, newTimeoutMs);
      setSnackbar({
        open: true,
        message: `Timeout updated for source <${source.name || keyId}>`,
        severity: "success",
      });
    } catch (error) {
      console.error("Error updating timeout:", error);
      setSnackbar({
        open: true,
        message: `Error updating timeout for source <${keyId}>`,
        severity: "error",
      });
    } finally {
      setGlobalUpdating(false);
    }
  };

  return (
    <>
      {error ? (
        <Typography variant="h6" color="error">
          {error}
        </Typography>
      ) : (
        <Grid container spacing={3}>
          {sources.map((source) => (
            <Grid item xs={12} sm={6} md={4} key={source.id}>
              <SectionCard title={source.name || source.id}>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  {t("current_timeout")}: {source.timeoutHours} {t("hours")} {t("and")} {source.timeoutMinutes} {t("minutes")}
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <TextField
                      label={t("hours")}
                      variant="outlined"
                      fullWidth
                      margin="normal"
                      type="number"
                      value={source.timeoutHours}
                      onChange={(e) => handleHoursChange(source.id, e.target.value)}
                      inputProps={{ min: 0 }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      label={t("minutes")}
                      variant="outlined"
                      fullWidth
                      margin="normal"
                      type="number"
                      value={source.timeoutMinutes}
                      onChange={(e) => handleMinutesChange(source.id, e.target.value)}
                      inputProps={{ min: 0, max: 59 }}
                    />
                  </Grid>
                </Grid>
                <Button variant="outlined" sx={getButtonStyle("open")} onClick={() => handleSave(source.id)} disabled={globalUpdating} fullWidth>
                  {t("save")}
                </Button>
              </SectionCard>
            </Grid>
          ))}
        </Grid>
      )}

      <Backdrop sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading || globalUpdating}>
        <CircularProgress color="inherit" />
      </Backdrop>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        TransitionComponent={Transition}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        sx={{ mt: { md: "2.5%" } }}
      >
        <Alert
          severity={snackbar.severity}
          variant="filled"
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          sx={{
            width: "100%",
            borderRadius: 2,
            boxShadow: 3,
            py: 1.5,
            px: 2,
            display: "flex",
            alignItems: "center",
            fontSize: "1rem",
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default AuthorizedKeysTimeoutSettings;
